import { EmptyState } from "@/components/widgets";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Lock, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { PanelCard, scopeLockToast } from "./primitives";
import { StarRating, RatingDelta } from "@/components/ratings/StarRating";

const RatingsBoard = ({ board, myOutletId, navigate, wide }) => (
  <PanelCard
    title={<span className="flex items-center gap-1.5"><Star size={15} className="text-[hsl(var(--cx-gold))]" fill="currentColor" strokeWidth={0} />Google Ratings — {board.function}</span>}
    testId={`google-ratings-board-${board.function.toLowerCase()}`}
    className={cn("col-span-2 order-[5.4] lg:order-none", wide ? "lg:col-span-12" : "lg:col-span-6")}>
    {board.rows.length === 0 ? (
      <EmptyState title="No locations in scope" />
    ) : (
      <div className="overflow-x-auto">
        <Table className="cx-table min-w-[520px]" data-testid={`google-ratings-table-${board.function.toLowerCase()}`}>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">#</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead className="text-right">vs 1st</TableHead>
              <TableHead className="text-right">Reviews</TableHead>
              <TableHead className="text-right pr-4">New MTD</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {board.rows.map((r) => {
              const locked = r.in_scope === false;
              return (
              <TableRow key={r.outlet_id}
                className={cn(locked ? "cursor-default" : "cursor-pointer",
                  r.rank === 1 && "bg-[hsl(var(--cx-gold)/0.10)]",
                  r.outlet_id === myOutletId && "bg-[hsl(var(--accent))] border-l-2 border-l-[hsl(var(--cx-gold))]")}
                data-locked={locked ? "true" : undefined}
                data-testid={`google-lb-row-${board.function.toLowerCase()}-${r.code}`}
                onClick={() => locked
                  ? scopeLockToast("Review details for this location are visible to its own team and leadership.")
                  : navigate("/google-ratings")}>
                <TableCell className="pl-4 tabular">{r.rank ?? "—"}</TableCell>
                <TableCell>
                  <span className={cn("font-medium", locked && "text-muted-foreground")}>{r.name}</span>{" "}
                  <span className="text-xs text-muted-foreground">{r.code}</span>
                  {locked && <Lock size={11} className="inline ml-1.5 -mt-0.5 text-muted-foreground/70" aria-label="details locked" />}
                  {r.outlet_id === myOutletId && (
                    <Badge variant="outline" className="ml-1.5 text-[0.6rem] border-[hsl(var(--cx-gold))] text-[hsl(var(--cx-gold))]">You</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {r.rating !== null && r.rating !== undefined ? (
                    <span className="flex items-center gap-1.5">
                      <span className="tabular font-semibold">{r.rating.toFixed(1)}</span>
                      <StarRating value={r.rating} size={12} />
                    </span>
                  ) : (
                    <Badge variant="outline" className="text-[0.6rem] text-muted-foreground">
                      {r.linked ? "No data yet" : "Not linked"}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right"><RatingDelta delta={r.rating_delta} /></TableCell>
                <TableCell className="text-right tabular">{r.total_reviews !== null && r.total_reviews !== undefined ? r.total_reviews.toLocaleString() : "—"}</TableCell>
                <TableCell className="text-right tabular pr-4">
                  +{r.mtd_new}{r.reviews_removed && <span title="Reviews removed by Google" className="text-[hsl(var(--status-amber))]"> ⚠</span>}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {board.climber && (
          <p className="px-5 py-2 text-xs font-medium text-[hsl(var(--cx-navy))] border-t bg-[hsl(var(--cx-gold)/0.06)]"
            data-testid={`google-lb-climber-${board.function.toLowerCase()}`}>
            {board.climber.line}
          </p>
        )}
        <p className="px-5 pt-1.5 pb-2 text-[0.65rem] text-muted-foreground">
          Recognition only — Google ratings never affect escalation or complaint standings.
        </p>
      </div>
    )}
  </PanelCard>
);

export const GoogleRatingsLeaderboard = ({ boards, myOutletId, navigate }) => {
  if (!boards) return null;
  const keys = Object.keys(boards);
  return keys.map((fn) => (
    <RatingsBoard key={fn} board={boards[fn]} myOutletId={myOutletId}
      navigate={navigate} wide={keys.length === 1} />
  ));
};
